import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge, RiskPill, fmtDate, fmtCurrency } from "../lib/status";
import { Plus, Search } from "lucide-react";

// `change_requested`, `is_hold`, and `at_risk` are filters on flags/fields,
// not on `status` itself: handled specially in the effect below rather
// than sent as a literal `status` query param.
const FILTERS = [
  { value: "all", label: "All" },
  { value: "awaiting_staff_approval", label: "Awaiting Approval" },
  { value: "awaiting_customer_confirmation", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "ongoing", label: "Ongoing" },
  { value: "change_requested", label: "Change Requested" },
  { value: "is_hold", label: "Active Holds" },
  { value: "at_risk", label: "At Risk" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No Show" },
  { value: "completed", label: "Completed" },
];
const CLIENT_ONLY_FILTERS = new Set(["all", "at_risk"]);

export default function Bookings() {
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    if (searchParams.get("change_requested") === "true") return "change_requested";
    if (searchParams.get("is_hold") === "true") return "is_hold";
    const s = searchParams.get("status");
    return FILTERS.some((f) => f.value === s) ? s : "all";
  });
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    let params = {};
    if (filter === "change_requested") params = { change_requested: true };
    else if (filter === "is_hold") params = { status: "awaiting_staff_approval", is_hold: true };
    else if (!CLIENT_ONLY_FILTERS.has(filter)) params = { status: filter };
    api.get("/bookings", { params })
      .then((r) => setBookings(r.data))
      .finally(() => setLoading(false));
  }, [filter]);

  const filtered = useMemo(() => {
    let list = bookings;
    if (filter === "at_risk") list = list.filter((b) => b.risk_status === "at_risk");
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter(
        (b) =>
          b.customer_name?.toLowerCase().includes(s) ||
          b.booking_reference?.toLowerCase().includes(s) ||
          b.customer_email?.toLowerCase().includes(s) ||
          b.vehicle?.name?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [bookings, filter, q]);

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Bookings</h1>
          <p className="text-slate-600 mt-1">Manage all bookings and their confirmation status.</p>
        </div>
        <Link to="/app/bookings/new" data-testid="bookings-new-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium">
          <Plus className="w-4 h-4" /> New booking
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="bookings-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, ref, email, vehicle…"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none text-sm w-72 max-w-full"
          />
        </div>
        <div className="flex flex-wrap gap-1 ml-auto">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              data-testid={`filter-${f.value}`}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filter === f.value
                  ? "bg-[#1B4332] text-white border-[#1B4332]"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-[0.16em] font-semibold text-slate-500">
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Dates</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading && (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-10 text-center" data-testid="bookings-empty">
                  <div className="text-slate-700 font-medium">No bookings yet</div>
                  <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                    When a customer messages, calls, or walks in, add the booking here so they can confirm it.
                  </p>
                  <Link to="/app/bookings/new" data-testid="bookings-empty-cta"
                         className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-lg btn-brand text-sm font-medium">
                    + Add Booking
                  </Link>
                </td></tr>
              )}
              {!loading && filtered.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`booking-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/app/bookings/${b.id}`} className="block">
                      <div className="font-medium text-slate-900">{b.customer_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{b.booking_reference}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/app/bookings/${b.id}`}>{b.vehicle?.name || "-"}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/app/bookings/${b.id}`}>
                      <div>{fmtDate(b.start_datetime)}</div>
                      <div className="text-xs text-slate-500">→ {fmtDate(b.end_datetime)}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono">
                    <Link to={`/app/bookings/${b.id}`}>{fmtCurrency(b.price)}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/app/bookings/${b.id}`} className="inline-flex items-center gap-2 flex-wrap">
                      <StatusBadge status={b.status} isHold={b.is_hold} changeRequested={b.change_requested}
                                   cancelledBy={b.cancelled_by} cancellationReason={b.cancellation_reason}
                                   reconfirmationRequired={b.reconfirmation_required} />
                      <RiskPill risk={b.risk_status} />
                      {b.damage_clarification_needed && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-900 border border-rose-200 inline-flex items-center gap-1"
                          data-testid={`booking-clarification-badge-${b.id}`}
                          title="Customer asked for clarification on an existing damage item"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-pulse" />
                          Damage clarification
                        </span>
                      )}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
